/**
 * One project: its files, its people, its chat.
 *
 * Deliberately the same layout as `Workspace.tsx` — files left, a 340px rail on
 * the right — because a project is the same idea one level down and looking
 * different would suggest it worked differently.
 *
 * It does differ in one way that matters, and the page has to be honest about
 * it: **a project's key is not the workspace's.** Somebody in the workspace who
 * is not on this project cannot open it, which is why the "not on this project"
 * state below is a real screen rather than a redirect. The server answers 404
 * for them (`accessFor` checks project membership first) and even if it did not,
 * they hold nothing that decrypts.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, type ProjectMemberWire } from '../api';
import { ds } from '../ds';
import { useRouter } from '../router';
import { Files } from './Files';
import { Chat } from '../components/Chat';
import { Footnote, Panel, PageTitle } from '../components/Shell';
import { assignMember, listProjects, projectKey, type DecryptedProject } from '../projects';
import { projectChannel } from '../workspaces';
import { monoPlain } from '../theme';
import { SkeletonLines } from '../components/Skeleton';
import { useT } from '../i18n';

const ROLES = ['lead', 'editor', 'commenter', 'viewer'] as const;

export function Project({
  workspaceId,
  projectId
}: {
  workspaceId: string;
  projectId: string;
}) {
  const t = useT();
  const { Button, Icon, Input } = ds();
  const { navigate } = useRouter();

  const [project, setProject] = useState<DecryptedProject | null>(null);
  const [members, setMembers] = useState<ProjectMemberWire[]>([]);
  const [key, setKey] = useState<CryptoKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof ROLES)[number]>('editor');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Listed rather than fetched by id: `listProjects` is what unwraps the
      // member envelope and seeds the node-key cache, so going through it means
      // the file browser can walk into the project immediately.
      const all = await listProjects(workspaceId);
      const found = all.find((p) => p.id === projectId) ?? null;
      setProject(found);
      setKey(projectKey(projectId) ?? null);

      if (found && !found.locked) {
        const res = await api.projects.members(projectId);
        setMembers(res.members);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open that project.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Panel style={{ padding: '26px 30px' }}>
        <SkeletonLines lines={4} label={t('project.opening-the-project')} />
      </Panel>
    );
  }

  if (!project || project.locked || !key) {
    return (
      <Panel style={{ padding: '40px 34px', display: 'grid', gap: 8 }}>
        <PageTitle title={t('project.you-are-not-on-this')} />
        <p style={{ margin: 0, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          {t('project.ask-a-project-lead-to')}
        </p>
      </Panel>
    );
  }

  const myRole = project.role;
  const canAssign = myRole === 'lead';
  const canPost = myRole !== 'viewer';

  const submitAssign = async () => {
    if (!email.trim()) return;
    setNotice(null);
    setError(null);
    try {
      const { pending } = await assignMember(projectId, email.trim(), role);
      setNotice(
        pending
          ? `${email.trim()} has no vault yet. They are on the project.`
          : `${email.trim()} can open this project now.`
      );
      setEmail('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add them.');
    }
  };

  const removeMember = async (memberId: string, label: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.projects.unassign(projectId, memberId);
      setNotice(`${label} was removed.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that member.');
    } finally {
      setBusy(false);
    }
  };

  const destroyProject = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.projects.remove(projectId);
      navigate(`/workspaces/${workspaceId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that project.');
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageTitle title={project.name} meta={`${members.length} on this project`} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 340px',
          gap: 18,
          alignItems: 'start'
        }}
      >
        <Files
          root={{
            id: project.rootNodeId,
            label: project.name,
            href: `/workspaces/${workspaceId}/projects/${projectId}`
          }}
        />

        <div style={{ display: 'grid', gap: 14 }}>
          <Panel style={{ padding: '18px 20px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="users" size={16} />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
                {t('project.on-this-project')}
              </span>
              <span style={{ ...monoPlain, marginLeft: 'auto' }}>{members.length}</span>
            </div>

            <div style={{ display: 'grid', gap: 6 }}>
              {members.map((m) => (
                <div
                  key={m.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                >
                  <span style={{ color: 'var(--text-strong)' }}>{m.email}</span>
                  <span style={{ ...monoPlain, marginLeft: 'auto' }}>
                    {m.role}
                    {/* Assigned is not the same as able to read. Say which. */}
                    {m.awaitingKey && ' · waiting for their vault'}
                  </span>
                  {canAssign && (
                    <button
                      onClick={() => void removeMember(m.id, m.email)}
                      disabled={busy}
                      aria-label={`Remove ${m.email}`}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-muted)'
                      }}
                    >
                      <Icon name="x" size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {canAssign && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitAssign();
                }}
                style={{ display: 'grid', gap: 8 }}
              >
                <Input
                  label={t('project.add-someone-from-the-workspace')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {ROLES.map((r) => (
                    <Button
                      key={r}
                      variant={r === role ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => setRole(r)}
                    >
                      {r}
                    </Button>
                  ))}
                </div>
                <Button type="submit" variant="secondary" size="sm">
                  {t('project.add')}
                </Button>
                <Footnote>{t('project.only-people-already-in-the')}</Footnote>
              </form>
            )}

            {notice && <Footnote>{notice}</Footnote>}
            {error && <Footnote>{error}</Footnote>}
          </Panel>

          {/* The project's own channel. A different key and a different AAD
              from the workspace's, so a message cannot be moved between them. */}
          <Chat
            workspaceId={workspaceId}
            channel={projectChannel(projectId, key)}
            members={members.map((m) => ({
              id: m.id,
              userId: m.userId,
              email: m.email,
              name: m.email,
              role: m.role as never,
              state: m.state,
              awaitingKey: m.awaitingKey
            })) as never}
            canPost={canPost}
          />

          {canAssign && (
            <Panel style={{ padding: '18px 20px', display: 'grid', gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
                {t('project.delete-this-project')}
              </span>
              <Footnote>{t('project.files-move-to-the-trash')}</Footnote>
              <div>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Delete “${project.name}”? Its files move to the trash.`)) {
                      void destroyProject();
                    }
                  }}
                >
                  {t('project.delete-project')}
                </Button>
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
